import { getStoredAccessToken } from "@/features/auth";
import { withCorrelationId } from "@/shared/services/correlation";
import {
  ensureAccessTokenFreshForApiCall,
  forceRefreshAccessTokenAfterUnauthorized,
  getKeycloakAccessToken,
} from "@/shared/services/keycloak";
import { API_CONFIG } from "@/shared/utils/constants";

export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Parsed JSON error body when the server returned one (else undefined).
     *  Lets callers read structured error payloads (e.g. a 409 confirmation
     *  prompt) instead of only the flattened message string. */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type SearchParamValue =
  | string
  | number
  | boolean
  | undefined
  | readonly (string | number)[];

export type SearchParamsInput = Record<string, SearchParamValue>;

type ApiErrorConstructor = new (
  status: number,
  message: string,
  body?: unknown,
) => Error;

/**
 * API root (e.g. `http://localhost:8000/v1`). Paths passed to `apiGet` etc.
 * should continue from here, such as `/vehicle-calendar/events/`.
 */
export function getApiBaseUrl(): string {
  const base = API_CONFIG.BASE_URL.trim();
  return base.replace(/\/$/, "");
}

/** Default JSON API headers including bearer token when available. */
export function getDefaultAuthHeaders(): Record<string, string> {
  const token = getKeycloakAccessToken() ?? getStoredAccessToken();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

export function getDefaultJsonHeaders(): Record<string, string> {
  return {
    ...getDefaultAuthHeaders(),
    "Content-Type": "application/json",
  };
}

/** Read an error response once, returning both a flattened message and the
 *  parsed JSON body (undefined when the body wasn't JSON). */
export async function readErrorFromResponse(
  res: Response,
): Promise<{ message: string; body: unknown }> {
  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { message: text || res.statusText, body: undefined };
  }
  if (typeof parsed === "object" && parsed !== null && "detail" in parsed) {
    const detail = (parsed as { detail: unknown }).detail;
    return {
      message: typeof detail === "string" ? detail : JSON.stringify(detail),
      body: parsed,
    };
  }
  if (typeof parsed === "object" && parsed !== null) {
    return { message: JSON.stringify(parsed), body: parsed };
  }
  return { message: text || res.statusText, body: parsed };
}

export async function readErrorDetailFromResponse(
  res: Response,
): Promise<string> {
  return (await readErrorFromResponse(res)).message;
}

export function errorMessageFromJsonBody(
  raw: unknown,
  fallback: string,
): string {
  if (typeof raw === "object" && raw !== null && "detail" in raw) {
    const detail = (raw as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    return JSON.stringify(detail);
  }
  if (typeof raw === "object" && raw !== null) {
    return JSON.stringify(raw);
  }
  return fallback;
}

function applySearchParams(url: URL, searchParams?: SearchParamsInput): void {
  if (!searchParams) return;
  for (const [key, value] of Object.entries(searchParams)) {
    if (value === undefined) continue;
    if (value === false) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        url.searchParams.append(key, String(item));
      }
      continue;
    }
    if (value === true) {
      url.searchParams.set(key, "true");
      continue;
    }
    url.searchParams.set(key, String(value));
  }
}

function buildAbsoluteUrl(path: string, searchParams?: SearchParamsInput): URL {
  const base = getApiBaseUrl();
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const absolute = `${base}${normalizedPath}`;
  const url = new URL(absolute);
  applySearchParams(url, searchParams);
  return url;
}

function throwWithApiError(
  ApiError: ApiErrorConstructor,
  status: number,
  message: string,
  body?: unknown,
): never {
  throw new ApiError(status, message, body);
}

async function parseJsonBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text.trim()) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return {};
  }
}

type CoreRequestAuthMode = "none" | "bearer" | "json";

interface CoreRequestOptions {
  searchParams?: SearchParamsInput;
  headers?: HeadersInit;
  signal?: AbortSignal;
  body?: BodyInit | null;
  ApiError?: ApiErrorConstructor;
  /** When false, do not read a JSON body on success (e.g. DELETE). Default: true */
  parseJson?: boolean;
  /** When true, return the raw response body as a Blob (e.g. CSV download). */
  parseBlob?: boolean;
  /**
   * When `parseBlob` is true, validates `Content-Type` before reading the body.
   * Throw from the callback to reject unexpected payloads (e.g. HTML error pages).
   */
  assertBlobContentType?: (contentType: string | null) => void;
  /** When set, refreshes the token if needed, then attaches default auth headers. */
  auth?: CoreRequestAuthMode;
  /** Internal: set after one 401 → refresh → retry cycle. */
  retriedAfter401?: boolean;
}

function resolveRequestHeaders(
  auth: CoreRequestAuthMode,
  headers?: HeadersInit,
): Record<string, string> {
  const base =
    auth === "json"
      ? getDefaultJsonHeaders()
      : auth === "bearer"
        ? getDefaultAuthHeaders()
        : {};
  // Attach a fresh correlation id so every API call can be traced end-to-end into
  // the backend logs; a caller's explicit id (any case) is preserved. Shared with
  // the unversioned auth-client via `withCorrelationId`.
  return withCorrelationId({ ...base, ...toRecord(headers) });
}

async function coreRequest(
  method: HttpMethod,
  path: string,
  options: CoreRequestOptions = {},
): Promise<unknown> {
  const {
    searchParams,
    headers,
    signal,
    body,
    ApiError = ApiClientError,
    parseJson = true,
    parseBlob = false,
    assertBlobContentType,
    auth = "none",
    retriedAfter401 = false,
  } = options;

  if (auth !== "none" && !retriedAfter401) {
    await ensureAccessTokenFreshForApiCall();
  }

  const url = buildAbsoluteUrl(path, searchParams);
  const res = await fetch(url.toString(), {
    method,
    headers: resolveRequestHeaders(auth, headers),
    body: body ?? null,
    signal,
  });

  if (res.status === 401 && auth !== "none" && !retriedAfter401) {
    const refreshed = await forceRefreshAccessTokenAfterUnauthorized();
    if (refreshed) {
      return coreRequest(method, path, {
        ...options,
        retriedAfter401: true,
      });
    }
  }

  if (!res.ok) {
    const { message, body: errorBody } = await readErrorFromResponse(res);
    throwWithApiError(ApiError, res.status, message, errorBody);
  }

  if (parseBlob) {
    assertBlobContentType?.(res.headers.get("content-type"));
    return res.blob();
  }

  if (!parseJson) {
    return undefined;
  }

  return parseJsonBody(res);
}

export interface ApiGetOptions {
  searchParams?: SearchParamsInput;
  headers?: HeadersInit;
  signal?: AbortSignal;
  ApiError?: ApiErrorConstructor;
}

export async function apiGet(
  path: string,
  options: ApiGetOptions = {},
): Promise<unknown> {
  const { searchParams, headers, signal, ApiError } = options;
  return coreRequest("GET", path, {
    searchParams,
    headers,
    signal,
    ApiError,
    parseJson: true,
    auth: "bearer",
  });
}

export interface ApiGetBlobOptions {
  searchParams?: SearchParamsInput;
  headers?: HeadersInit;
  signal?: AbortSignal;
  ApiError?: ApiErrorConstructor;
  assertBlobContentType?: (contentType: string | null) => void;
}

/** GET that returns the raw response body as a Blob (e.g. a CSV download). */
export async function apiGetBlob(
  path: string,
  options: ApiGetBlobOptions = {},
): Promise<Blob> {
  const { searchParams, headers, signal, ApiError, assertBlobContentType } =
    options;
  return (await coreRequest("GET", path, {
    searchParams,
    headers,
    signal,
    ApiError,
    parseBlob: true,
    assertBlobContentType,
    auth: "bearer",
  })) as Blob;
}

export interface ApiDeleteOptions {
  searchParams?: SearchParamsInput;
  headers?: HeadersInit;
  signal?: AbortSignal;
  ApiError?: ApiErrorConstructor;
}

export async function apiDelete(
  path: string,
  options: ApiDeleteOptions = {},
): Promise<void> {
  const { searchParams, headers, signal, ApiError } = options;
  await coreRequest("DELETE", path, {
    searchParams,
    headers,
    signal,
    ApiError,
    parseJson: false,
    auth: "bearer",
  });
}

export interface ApiPostOptions {
  searchParams?: SearchParamsInput;
  headers?: HeadersInit;
  signal?: AbortSignal;
  ApiError?: ApiErrorConstructor;
  /** Serialized as JSON; sets `Content-Type: application/json`. */
  jsonBody?: unknown;
}

export async function apiPost(
  path: string,
  options: ApiPostOptions = {},
): Promise<unknown> {
  const { searchParams, headers, signal, ApiError, jsonBody } = options;
  const hasJson = jsonBody !== undefined;
  return coreRequest("POST", path, {
    searchParams,
    headers,
    signal,
    body: hasJson ? JSON.stringify(jsonBody) : undefined,
    ApiError,
    parseJson: true,
    auth: hasJson ? "json" : "bearer",
  });
}

export interface ApiPutOptions {
  searchParams?: SearchParamsInput;
  headers?: HeadersInit;
  signal?: AbortSignal;
  ApiError?: ApiErrorConstructor;
  jsonBody: unknown;
}

export async function apiPut(
  path: string,
  options: ApiPutOptions,
): Promise<unknown> {
  const { searchParams, headers, signal, ApiError, jsonBody } = options;
  return coreRequest("PUT", path, {
    searchParams,
    headers,
    signal,
    body: JSON.stringify(jsonBody),
    ApiError,
    parseJson: true,
    auth: "json",
  });
}

export interface ApiPatchOptions {
  searchParams?: SearchParamsInput;
  headers?: HeadersInit;
  signal?: AbortSignal;
  ApiError?: ApiErrorConstructor;
  jsonBody: unknown;
}

export async function apiPatch(
  path: string,
  options: ApiPatchOptions,
): Promise<unknown> {
  const { searchParams, headers, signal, ApiError, jsonBody } = options;
  return coreRequest("PATCH", path, {
    searchParams,
    headers,
    signal,
    body: JSON.stringify(jsonBody),
    ApiError,
    parseJson: true,
    auth: "json",
  });
}

function toRecord(headers?: HeadersInit): Record<string, string> {
  if (!headers) return {};
  if (headers instanceof Headers) {
    const out: Record<string, string> = {};
    headers.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return { ...headers };
}
